import { KratosUser } from '@/contexts/AuthContext'
import { fetchWithAuth } from '@/utils/api-error-handler'

const AGENTS_PROXY_BASE = '/api/agents-v2/v1'
const BACKEND_API_URL = process.env.NEXT_PUBLIC_API_URL || ''
export const MAX_THREAD_FILE_SIZE_BYTES = 40 * 1024 * 1024

export type AgentsProfile = {
  id: string
  name: string
  owner_user_id: string
  team_id?: string | null
  model: string
  researcher_model: string
  skills: string[]
  sandbox: AgentsSandboxConfig
}

export type AgentsSandboxConfig = {
  sandbox_type: 'gpu' | 'cpu'
  sandbox_gpu: string
  sandbox_image_gpu: string
  sandbox_image_cpu: string
  sandbox_pip_packages: string[]
  cpu_cores: number
  ram_gb: number
  scope: 'thread' | 'assistant'
  idle_timeout_seconds: number
  timeout_seconds: number
}

export type AgentsThread = {
  id: string
  owner_user_id: string
  team_id?: string | null
  profile_id: string
  title: string
  project_name?: string | null
  status: 'idle' | 'busy' | 'error' | 'interrupted'
  organize_label?: 'pinned' | 'archive' | null
  created_at: string
  updated_at: string
  last_message_at?: string | null
  metadata?: Record<string, string> | null
}

export type ThreadShareVisibility = 'private' | 'public_link' | 'restricted_email'

export type ThreadShareSettings = {
  thread_id: string
  share_id: string | null
  visibility: ThreadShareVisibility
  allowed_emails: string[]
  fork_count: number
  star_count: number
  viewer_starred: boolean
  shared_at: string | null
  shared_by?: {
    name?: string | null
    avatar_url?: string | null
  } | null
}

export type SharedThreadBootstrap = {
  thread: AgentsThread
  share: ThreadShareSettings
  viewer?: {
    authenticated: boolean
    email?: string | null
  } | null
}

export type SharedThreadForkResult = {
  thread: AgentsThread
  source_thread_id: string
  source_share_id: string
  copied_messages: number
  copied_todos: number
  copied_files: number
  fork_count: number
}

export type SharedThreadStarResult = {
  thread_id: string
  share_id: string
  starred: boolean
  star_count: number
}

export type AgentsMessage = {
  id: string
  thread_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  metadata?: Record<string, string>
  created_at: string
}

export type AgentsTodo = {
  id: string
  thread_id: string
  content: string
  status: 'pending' | 'in_progress' | 'completed'
  active_form?: string | null
  updated_at: string
}

export type ThreadContextUsageBreakdown = {
  model_key: string
  model_context_window: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  reasoning_tokens: number
  total_tokens: number
  usage_percent: number
}

export type ThreadContextUsage = {
  thread_id: string
  scope: 'current_model' | 'all_models'
  model_key: string
  model_context_window: number
  input_tokens: number
  output_tokens: number
  cached_tokens: number
  reasoning_tokens: number
  total_tokens: number
  usage_percent: number
  updated_at: string
  model_breakdown?: ThreadContextUsageBreakdown[]
}

export type AgentsThreadFile = {
  path: string
  size: number
  mtime?: string | null
}

export type AgentSkillDefinition = {
  id: string
  name: string
  display_name?: string | null
  description?: string | null
  repo_url?: string | null
  category?: string | null
}

export type ThreadRuntimeSkill = {
  name: string
  sandbox_skill_dir: string
  has_skill_md: boolean
  description?: string | null
  icon_src?: string | null
  repo_url?: string | null
  source?: string | null
  // Catalog metadata surfaced by the agents-api (avoids browser GitHub calls).
  author?: string | null
  version?: string | null
  release_date?: string | null
}

export type AgentsStreamEvent = {
  type: string
  [key: string]: unknown
}

export type ValidateImageResult = {
  image: string
  registry: string
  exists: boolean
  ok: boolean
  message?: string | null
}

export type AgentsSandbox = {
  id: string
  owner_user_id: string
  team_id?: string | null
  profile_id: string
  thread_id?: string | null
  provider: 'mock' | 'modal'
  provider_sandbox_name: string
  sandbox_type: 'gpu' | 'cpu'
  sandbox_gpu: string
  cpu_cores: number
  ram_gb: number
  gpu_vram_gb?: number | null
  status: 'creating' | 'running' | 'stopping' | 'stopped' | 'failed'
  scope: 'thread' | 'assistant'
  created_at: string
  updated_at: string
}

export type AgentSandboxUserConfig = {
  id: string
  user_id: string
  team_id?: string | null
  sandbox_image_cpu: string | null
  sandbox_image_gpu: string | null
  pip_packages: string[]
  created_at: string
  updated_at: string
}

function sanitizeHeaderText(value: string, maxLength = 1024): string {
  const normalized = String(value || '').trim().normalize('NFC')
  if (!normalized) return ''
  // Recover common surrogateescape artifacts (U+DC80..U+DCFF) back to their
  // original Latin-1 byte/codepoint and drop any other invalid lone surrogates.
  const repaired = Array.from(normalized)
    .map((char) => {
      const codePoint = char.codePointAt(0)
      if (codePoint == null) return ''
      if (codePoint >= 0xdc80 && codePoint <= 0xdcff) return String.fromCodePoint(codePoint - 0xdc00)
      if (codePoint >= 0xd800 && codePoint <= 0xdfff) return ''
      return char
    })
    .join('')
    .trim()
  return repaired.slice(0, maxLength)
}

function buildHeaders(user: KratosUser, teamId: string | null): HeadersInit {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    'x-user-id': String(user.id),
  }
  const email = sanitizeHeaderText(String(user.traits?.email || ''), 320)
  if (email) headers['x-user-email'] = email
  const first = sanitizeHeaderText(String(user.traits?.name?.first || ''), 255)
  const last = sanitizeHeaderText(String(user.traits?.name?.last || ''), 255)
  const fullName = [first, last].filter(Boolean).join(' ').trim()
  if (fullName) headers['x-user-name'] = fullName
  const avatarUrl = sanitizeHeaderText(String(user.traits?.picture || ''), 1024)
  if (avatarUrl) headers['x-user-avatar'] = avatarUrl
  if (teamId) headers['x-team-id'] = teamId
  return headers
}

async function request<T>(
  user: KratosUser,
  teamId: string | null,
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${AGENTS_PROXY_BASE}${path}`, {
    ...options,
    headers: {
      ...buildHeaders(user, teamId),
      ...(options.headers || {}),
    },
    cache: 'no-store',
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Agents API ${response.status}: ${text || response.statusText}`)
  }
  return (await response.json()) as T
}

async function requestForm<T>(
  user: KratosUser,
  teamId: string | null,
  path: string,
  form: FormData,
  options: RequestInit = {}
): Promise<T> {
  const headers = buildHeaders(user, teamId)
  delete (headers as Record<string, string>)['content-type']
  const response = await fetch(`${AGENTS_PROXY_BASE}${path}`, {
    ...options,
    method: options.method ?? 'POST',
    body: form,
    headers: {
      ...headers,
      ...(options.headers || {}),
    },
    cache: 'no-store',
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Agents API ${response.status}: ${text || response.statusText}`)
  }
  return (await response.json()) as T
}

export class AgentsApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'AgentsApiError'
    this.status = status
  }
}

type SharedViewerContext = {
  user?: KratosUser | null
  teamId?: string | null
}

async function requestShared<T>(
  path: string,
  options: RequestInit = {},
  viewer?: SharedViewerContext
): Promise<T> {
  const sharedHeaders: HeadersInit = viewer?.user ? buildHeaders(viewer.user, viewer.teamId ?? null) : {}
  const response = await fetch(`${AGENTS_PROXY_BASE}${path}`, {
    ...options,
    headers: {
      ...sharedHeaders,
      ...(options.headers || {}),
    },
    cache: 'no-store',
  })
  if (!response.ok) {
    const text = await response.text()
    throw new AgentsApiError(response.status, `Agents API ${response.status}: ${text || response.statusText}`)
  }
  return (await response.json()) as T
}

async function backendRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  if (!BACKEND_API_URL) {
    throw new Error('NEXT_PUBLIC_API_URL is not configured.')
  }
  const response = await fetchWithAuth(`${BACKEND_API_URL}${path}`, {
    ...options,
    headers: {
      'content-type': 'application/json',
      ...(options.headers || {}),
    },
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Backend API ${response.status}: ${text || response.statusText}`)
  }
  return (await response.json()) as T
}

export async function listProfiles(user: KratosUser, teamId: string | null) {
  return request<{ data: AgentsProfile[]; count: number }>(user, teamId, '/profiles')
}

export async function createProfile(
  user: KratosUser,
  teamId: string | null,
  payload: {
    name: string
    model?: string
    researcher_model?: string
    team_id?: string | null
  }
) {
  return request<AgentsProfile>(user, teamId, '/profiles', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function patchProfileSandboxConfig(
  user: KratosUser,
  teamId: string | null,
  profileId: string,
  payload: Partial<AgentsSandboxConfig>
) {
  return request<AgentsSandboxConfig>(user, teamId, `/profiles/${profileId}/sandbox-config`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function getSandboxUserConfig(user: KratosUser, teamId: string | null) {
  const query = teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''
  return backendRequest<AgentSandboxUserConfig>(`/v1/sandbox-config${query}`)
}

export async function updateSandboxUserConfig(
  user: KratosUser,
  teamId: string | null,
  payload: { sandbox_image_cpu?: string; sandbox_image_gpu?: string; pip_packages?: string[] }
) {
  const query = teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''
  return backendRequest<AgentSandboxUserConfig>(`/v1/sandbox-config${query}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function patchProfile(
  user: KratosUser,
  teamId: string | null,
  profileId: string,
  payload: {
    name?: string
    model?: string
    researcher_model?: string
    runtime_context?: Record<string, string>
  }
) {
  return request<AgentsProfile>(user, teamId, `/profiles/${profileId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function validateSandboxImage(
  user: KratosUser,
  teamId: string | null,
  image: string
) {
  return backendRequest<ValidateImageResult>('/v1/images/validate', {
    method: 'POST',
    body: JSON.stringify({ image }),
  })
}

export async function createThread(
  user: KratosUser,
  teamId: string | null,
  payload: { profile_id: string; title: string; metadata?: Record<string, string> }
) {
  return request<AgentsThread>(user, teamId, '/threads', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function getProfile(user: KratosUser, teamId: string | null, profileId: string) {
  return request<AgentsProfile>(user, teamId, `/profiles/${profileId}`)
}

export async function listSkillDefinitions(user: KratosUser, teamId: string | null) {
  const query = teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''
  return backendRequest<AgentSkillDefinition[]>(`/v1/skills${query}`)
}

export async function getSkillFileContent(
  user: KratosUser,
  teamId: string | null,
  skillId: string,
  path: string
) {
  const query = new URLSearchParams({ path })
  if (teamId) query.set('team_id', teamId)
  const response = await fetchWithAuth(`${BACKEND_API_URL}/v1/skills/${skillId}/files?${query.toString()}`, {
    method: 'GET',
    cache: 'no-store',
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Backend API ${response.status}: ${text || response.statusText}`)
  }
  return response.text()
}

export async function listThreadRuntimeSkills(
  user: KratosUser,
  teamId: string | null,
  threadId: string
) {
  return request<{ data: ThreadRuntimeSkill[]; count: number }>(
    user,
    teamId,
    `/threads/${threadId}/skills/runtime`
  )
}

export async function getThreadRuntimeSkillMarkdown(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  skillName: string
) {
  const encoded = encodeURIComponent(skillName)
  return request<{ name: string; sandbox_path: string; content: string | null }>(
    user,
    teamId,
    `/threads/${threadId}/skills/runtime/${encoded}/skill-md`
  )
}

export async function listRuntimeSkills(user: KratosUser, teamId: string | null) {
  return request<{ data: ThreadRuntimeSkill[]; count: number }>(user, teamId, '/skills/runtime')
}

export async function getRuntimeSkillMarkdown(
  user: KratosUser,
  teamId: string | null,
  skillName: string
) {
  const encoded = encodeURIComponent(skillName)
  return request<{ name: string; sandbox_path: string; content: string | null }>(
    user,
    teamId,
    `/skills/runtime/${encoded}/skill-md`
  )
}

export async function getThread(user: KratosUser, teamId: string | null, threadId: string) {
  return request<AgentsThread>(user, teamId, `/threads/${threadId}`)
}

export async function getThreadShareSettings(user: KratosUser, teamId: string | null, threadId: string) {
  return request<ThreadShareSettings>(user, teamId, `/threads/${threadId}/share`)
}

export async function updateThreadShareSettings(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  payload: {
    visibility: ThreadShareVisibility
    allowed_emails?: string[]
  },
) {
  return request<ThreadShareSettings>(user, teamId, `/threads/${threadId}/share`, {
    method: 'PUT',
    body: JSON.stringify({
      visibility: payload.visibility,
      allowed_emails: payload.allowed_emails ?? [],
    }),
  })
}

export async function getSharedThread(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<SharedThreadBootstrap>(`/shares/${encoded}`, {}, opts?.viewer)
}

export async function getSharedThreadHistory(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<{ thread_id: string; share_id: string; messages: AgentsMessage[]; todos?: AgentsTodo[]; count: number }>(
    `/shares/${encoded}/history`,
    {},
    opts?.viewer
  )
}

export async function getSharedThreadContextUsage(
  shareId: string,
  opts?: {
    scope?: 'current_model' | 'all_models'
    modelKey?: string
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  const params = new URLSearchParams()
  params.set('scope', opts?.scope ?? 'current_model')
  if (opts?.modelKey) params.set('model_key', opts.modelKey)
  return requestShared<ThreadContextUsage>(`/shares/${encoded}/context-usage?${params.toString()}`, {}, opts?.viewer)
}

export async function getSharedThreadSandboxStatus(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<{
    status: string
    phase?: string | null
    detail?: string | null
    ready: boolean
    sandbox: AgentsSandbox | null
  }>(`/shares/${encoded}/sandbox/status`, {}, opts?.viewer)
}

export async function forkSharedThread(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<SharedThreadForkResult>(
    `/shares/${encoded}/fork`,
    {
      method: 'POST',
    },
    opts?.viewer
  )
}

export async function starSharedThread(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<SharedThreadStarResult>(
    `/shares/${encoded}/star`,
    {
      method: 'POST',
    },
    opts?.viewer
  )
}

export async function unstarSharedThread(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<SharedThreadStarResult>(
    `/shares/${encoded}/star`,
    {
      method: 'DELETE',
    },
    opts?.viewer
  )
}

export async function listSharedThreadFiles(
  shareId: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<{ data: AgentsThreadFile[]; folders?: string[]; count: number }>(
    `/shares/${encoded}/files`,
    {},
    opts?.viewer
  )
}

export async function getSharedThreadFileDownload(
  shareId: string,
  path: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encodedShareId = encodeURIComponent(shareId)
  const encodedPath = encodeURIComponent(path)
  return requestShared<{ url: string }>(`/shares/${encodedShareId}/files/download?path=${encodedPath}`, {}, opts?.viewer)
}

export async function getSharedThreadFileContent(
  shareId: string,
  path: string,
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encodedShareId = encodeURIComponent(shareId)
  const encodedPath = encodeURIComponent(path)
  return requestShared<AgentsThreadFileContent>(
    `/shares/${encodedShareId}/files/content?path=${encodedPath}`,
    {},
    opts?.viewer
  )
}

export async function listThreadFiles(user: KratosUser, teamId: string | null, threadId: string) {
  return request<{ data: AgentsThreadFile[]; folders?: string[]; count: number }>(
    user,
    teamId,
    `/threads/${threadId}/files`
  )
}

export async function getThreadFileDownload(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  path: string
) {
  const encoded = encodeURIComponent(path)
  return request<{ url: string }>(user, teamId, `/threads/${threadId}/files/download?path=${encoded}`)
}

export async function uploadThreadFile(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  file: File,
  relativeDir?: string,
  onProgress?: (uploadedBytes: number, totalBytes: number) => void
) {
  if (file.size > MAX_THREAD_FILE_SIZE_BYTES) {
    throw new Error('File is too large. Max size is 40MB per file.')
  }
  const form = new FormData()
  form.append('file', file)
  if (relativeDir) {
    form.append('relative_dir', relativeDir)
  }
  if (typeof XMLHttpRequest === 'undefined') {
    const result = await requestForm<{ path: string; count: number }>(
      user,
      teamId,
      `/threads/${threadId}/files/upload`,
      form
    )
    onProgress?.(file.size, file.size)
    return result
  }

  return new Promise<{ path: string; count: number }>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${AGENTS_PROXY_BASE}/threads/${threadId}/files/upload`, true)

    const headers = buildHeaders(user, teamId) as Record<string, string>
    for (const [key, value] of Object.entries(headers)) {
      if (key.toLowerCase() === 'content-type') continue
      xhr.setRequestHeader(key, value)
    }

    xhr.upload.onprogress = (event) => {
      const loaded = event.lengthComputable
        ? Math.min(event.loaded, file.size)
        : Math.min(event.loaded || 0, file.size)
      onProgress?.(loaded, file.size)
    }

    xhr.onerror = () => {
      reject(new Error('Agents API upload failed: network error'))
    }

    xhr.onload = () => {
      const text = xhr.responseText || ''
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Agents API ${xhr.status}: ${text || xhr.statusText}`))
        return
      }
      try {
        const parsed = JSON.parse(text) as { path: string; count: number }
        onProgress?.(file.size, file.size)
        resolve(parsed)
      } catch {
        reject(new Error(`Agents API ${xhr.status}: Invalid JSON response`))
      }
    }

    xhr.send(form)
  })
}

export async function createThreadFolder(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  path: string
) {
  return request<{ path: string }>(user, teamId, `/threads/${threadId}/files/folders`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  })
}

export async function moveThreadFile(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  sourcePath: string,
  targetPath: string
) {
  return request<{ source_path: string; target_path: string }>(user, teamId, `/threads/${threadId}/files/move`, {
    method: 'POST',
    body: JSON.stringify({ source_path: sourcePath, target_path: targetPath }),
  })
}

export async function deleteThreadFile(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  path: string,
  opts?: { recursive?: boolean }
) {
  return request<{ path: string }>(user, teamId, `/threads/${threadId}/files`, {
    method: 'DELETE',
    body: JSON.stringify({ path, recursive: opts?.recursive ?? false }),
  })
}

export type AgentsThreadFileContent = {
  path: string
  content: string
  version: string
  size: number
  mtime?: string | null
  source: 'sandbox' | 's3'
}

export type PutThreadFileContentResult =
  | { ok: true; content: AgentsThreadFileContent }
  | { ok: false; conflict: true; current: AgentsThreadFileContent }

export async function getThreadFileContent(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  path: string
) {
  const encoded = encodeURIComponent(path)
  return request<AgentsThreadFileContent>(
    user,
    teamId,
    `/threads/${threadId}/files/content?path=${encoded}`
  )
}

export async function putThreadFileContent(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  payload: { path: string; content: string; base_version?: string | null }
): Promise<PutThreadFileContentResult> {
  const response = await fetch(`${AGENTS_PROXY_BASE}/threads/${threadId}/files/content`, {
    method: 'PUT',
    headers: buildHeaders(user, teamId),
    body: JSON.stringify(payload),
    cache: 'no-store',
  })
  if (response.status === 409) {
    const body = (await response.json()) as { current: AgentsThreadFileContent }
    return { ok: false, conflict: true, current: body.current }
  }
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Agents API ${response.status}: ${text || response.statusText}`)
  }
  const content = (await response.json()) as AgentsThreadFileContent
  return { ok: true, content }
}

export async function updateThread(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  payload: { title?: string; organize_label?: 'pinned' | 'archive' | null; metadata?: Record<string, string> | null },
) {
  return request<AgentsThread>(user, teamId, `/threads/${threadId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function autoTitleThread(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  payload: { force?: boolean } = {}
) {
  return request<{ title: string }>(user, teamId, `/threads/${threadId}/title/auto`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export type AutoInspectThreadResult = {
  summary_markdown: string
  mermaid: string
  tool_calls: string[]
  tool_calls_count: number
  base_model_key?: string
}

export async function autoInspectThread(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  payload: { include_tool_flow?: boolean } = {}
) {
  return request<AutoInspectThreadResult>(user, teamId, `/threads/${threadId}/inspect/auto`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function autoInspectSharedThread(
  shareId: string,
  payload: { include_tool_flow?: boolean } = {},
  opts?: {
    viewer?: SharedViewerContext
  },
) {
  const encoded = encodeURIComponent(shareId)
  return requestShared<AutoInspectThreadResult>(
    `/shares/${encoded}/inspect/auto`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'content-type': 'application/json',
      },
    },
    opts?.viewer
  )
}

export async function listThreads(
  user: KratosUser,
  teamId: string | null,
  opts?: { cursor?: string | null; limit?: number },
) {
  const params = new URLSearchParams()
  if (opts?.cursor) params.set('cursor', opts.cursor)
  if (opts?.limit) params.set('limit', String(opts.limit))
  const query = params.toString()
  return request<{ data: AgentsThread[]; count: number; next_cursor?: string | null }>(
    user,
    teamId,
    `/threads${query ? `?${query}` : ''}`
  )
}

/** Admin-only: list agent threads owned by a user across all team scopes. */
export async function listAdminUserThreads(
  user: KratosUser,
  ownerUserId: string,
  opts?: { cursor?: string | null; limit?: number },
) {
  const params = new URLSearchParams()
  if (opts?.cursor) params.set('cursor', opts.cursor)
  if (opts?.limit) params.set('limit', String(opts.limit))
  const query = params.toString()
  const ownerId = encodeURIComponent(String(ownerUserId || '').trim())
  return request<{ data: AgentsThread[]; count: number; next_cursor?: string | null }>(
    user,
    null,
    `/admin/users/${ownerId}/threads${query ? `?${query}` : ''}`
  )
}

export async function searchThreads(
  user: KratosUser,
  teamId: string | null,
  q?: string,
  opts?: { limit?: number; signal?: AbortSignal },
) {
  const params = new URLSearchParams()
  const limit = Math.min(50, Math.max(1, opts?.limit ?? 20))
  params.set('limit', String(limit))
  const trimmed = String(q || '').trim()
  if (trimmed.length >= 2) params.set('q', trimmed)
  const query = params.toString()
  return request<{ q: string; count: number; results: AgentsThread[] }>(
    user,
    teamId,
    `/threads/search${query ? `?${query}` : ''}`,
    { signal: opts?.signal },
  )
}

export async function deleteThread(user: KratosUser, teamId: string | null, threadId: string) {
  const response = await fetch(`${AGENTS_PROXY_BASE}/threads/${threadId}`, {
    method: 'DELETE',
    headers: buildHeaders(user, teamId),
    cache: 'no-store',
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Agents API ${response.status}: ${text || response.statusText}`)
  }
  return true
}

export async function getThreadHistory(user: KratosUser, teamId: string | null, threadId: string) {
  return request<{ messages: AgentsMessage[]; todos?: AgentsTodo[]; feedback?: Record<string, 'up' | 'down'>; count: number }>(
    user,
    teamId,
    `/threads/${threadId}/history`
  )
}

export async function getThreadTodos(user: KratosUser, teamId: string | null, threadId: string) {
  return request<{ todos: AgentsTodo[]; count: number }>(user, teamId, `/threads/${threadId}/todos`)
}

export async function getThreadContextUsage(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  opts: {
    scope?: 'current_model' | 'all_models'
    modelKey?: string
  } = {}
) {
  const params = new URLSearchParams()
  params.set('scope', opts.scope ?? 'current_model')
  if (opts.modelKey) params.set('model_key', opts.modelKey)
  return request<ThreadContextUsage>(
    user,
    teamId,
    `/threads/${threadId}/context-usage?${params.toString()}`
  )
}

export async function sendThreadMessage(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  content: string
) {
  return request<{ thread_id: string; message: AgentsMessage; reply: AgentsMessage }>(
    user,
    teamId,
    `/threads/${threadId}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ content }),
    }
  )
}

export async function upsertThreadMessageFeedback(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  messageId: string,
  reaction: 'up' | 'down' | null
) {
  return request<{ thread_id: string; message_id: string; reaction: 'up' | 'down' | null; updated_at: string }>(
    user,
    teamId,
    `/threads/${threadId}/messages/${messageId}/feedback`,
    {
      method: 'POST',
      body: JSON.stringify({ reaction }),
    }
  )
}

export async function streamThreadMessage(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  content: string,
  handlers: {
    onEvent: (event: AgentsStreamEvent) => void
    signal?: AbortSignal
    feedbackKeys?: string[]
  },
) {
  const response = await fetch(`${AGENTS_PROXY_BASE}/threads/${threadId}/messages/stream`, {
    method: 'POST',
    headers: buildHeaders(user, teamId),
    body: JSON.stringify({
      content,
      ...(handlers.feedbackKeys?.length ? { feedback_keys: handlers.feedbackKeys } : {}),
    }),
    cache: 'no-store',
    signal: handlers.signal,
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Agents API ${response.status}: ${text || response.statusText}`)
  }
  if (!response.body) {
    throw new Error('Streaming response body is empty')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const chunks = buffer.split('\n\n')
    buffer = chunks.pop() || ''
    for (const chunk of chunks) {
      const lines = chunk.split('\n')
      let eventName = 'message'
      let dataText = ''
      for (const line of lines) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim()
        if (line.startsWith('data:')) dataText += line.slice(5).trim()
      }
      if (!dataText) continue
      try {
        const parsed = JSON.parse(dataText) as Record<string, unknown>
        handlers.onEvent({ type: eventName, ...parsed })
      } catch {
        handlers.onEvent({ type: eventName, raw: dataText })
      }
    }
  }
}

export async function closeThreadSession(user: KratosUser, teamId: string | null, threadId: string) {
  return request<{ thread_id: string; closed: boolean; stopped_count: number }>(
    user,
    teamId,
    `/threads/${threadId}/close`,
    {
      method: 'POST',
      body: JSON.stringify({}),
    }
  )
}

export async function interruptThreadSession(user: KratosUser, teamId: string | null, threadId: string) {
  return request<{ thread_id: string; status: 'interrupted' }>(user, teamId, `/threads/${threadId}/interrupt`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function startThreadSandbox(
  user: KratosUser,
  teamId: string | null,
  threadId: string,
  payload: Partial<AgentsSandboxConfig> = {}
) {
  return request<{
    thread_id: string
    started: boolean
    status?: string
    ready?: boolean
    detail?: string | null
    sandbox: AgentsSandbox | null
  }>(
    user,
    teamId,
    `/threads/${threadId}/sandbox/start`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  )
}

export async function getThreadSandboxStatus(user: KratosUser, teamId: string | null, threadId: string) {
  return request<{
    status: string
    phase?: string | null
    detail?: string | null
    ready: boolean
    sandbox: AgentsSandbox | null
  }>(user, teamId, `/threads/${threadId}/sandbox/status`)
}

export async function listSandboxes(user: KratosUser, teamId: string | null) {
  return request<{ data: AgentsSandbox[]; count: number }>(user, teamId, '/sandboxes')
}

export async function stopSandbox(user: KratosUser, teamId: string | null, sandboxId: string) {
  return request<AgentsSandbox>(user, teamId, `/sandboxes/${sandboxId}/stop`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function createEnvironmentLease(
  user: KratosUser,
  teamId: string | null,
  threadId: string
) {
  return request<{ id: string; thread_id: string; expires_at: string; values: Record<string, string> }>(
    user,
    teamId,
    `/threads/${threadId}/environment`,
    {
      method: 'POST',
      body: JSON.stringify({ include_fastfold_api_key: true }),
    }
  )
}

export type IntegrationProviderField = {
  id: string
  provider_key: string
  env_name: string
  label: string
  is_secret: boolean
  is_required: boolean
  default_value?: string | null
  description?: string | null
  sort_order: number
  is_enabled: boolean
}

export type IntegrationProviderValue = {
  id: string
  provider_key: string
  scope_type: 'user' | 'team'
  scope_id: string
  env_name: string
  is_active: boolean
  has_value: boolean
  masked_value: string
  updated_at: string
}

export type IntegrationProvider = {
  provider_key: string
  display_name: string
  icon_src?: string | null
  api_key_url?: string | null
  fields: IntegrationProviderField[]
}

export type IntegrationProviderWithValues = IntegrationProvider & {
  values: IntegrationProviderValue[]
}

export type IntegrationCustomEnv = {
  id: string
  scope_type: 'user' | 'team'
  scope_id: string
  label: string
  env_name: string
  value_secret?: string | null
  value_plaintext?: string | null
  inject_to_sandbox: boolean
  write_to_workspace_env: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

async function requestNoContent(
  user: KratosUser,
  teamId: string | null,
  path: string,
  options: RequestInit = {}
) {
  const response = await fetch(`${AGENTS_PROXY_BASE}${path}`, {
    ...options,
    headers: {
      ...buildHeaders(user, teamId),
      ...(options.headers || {}),
    },
    cache: 'no-store',
  })
  if (!response.ok && response.status !== 204) {
    const text = await response.text()
    throw new Error(`Agents API ${response.status}: ${text || response.statusText}`)
  }
  return true
}

export async function listIntegrationProviders(user: KratosUser, teamId: string | null) {
  return request<{ data: IntegrationProvider[]; count: number }>(user, teamId, '/integrations/providers')
}

export async function listIntegrationSecrets(
  user: KratosUser,
  teamId: string | null,
  opts?: { providerKey?: string }
) {
  const params = new URLSearchParams()
  if (teamId) params.set('team_id', teamId)
  if (opts?.providerKey) params.set('provider_key', opts.providerKey)
  const query = params.toString()
  return request<{ data: IntegrationProviderWithValues[]; count: number }>(
    user,
    teamId,
    `/integrations/secrets${query ? `?${query}` : ''}`
  )
}

export async function upsertIntegrationSecrets(
  user: KratosUser,
  teamId: string | null,
  providerKey: string,
  values: Record<string, string>
) {
  return request<{ provider_key: string; count: number }>(
    user,
    teamId,
    `/integrations/secrets/${providerKey}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        team_id: teamId,
        values,
      }),
    }
  )
}

export async function validateIntegrationSecrets(
  user: KratosUser,
  teamId: string | null,
  providerKey: string,
  values: Record<string, string>
) {
  return request<{ ok: boolean; error_message?: string; latency_ms: number }>(
    user,
    teamId,
    `/integrations/secrets/${providerKey}/validate`,
    {
      method: 'POST',
      body: JSON.stringify({
        team_id: teamId,
        values,
      }),
    }
  )
}

export async function deleteIntegrationSecret(
  user: KratosUser,
  teamId: string | null,
  providerKey: string,
  envName: string
) {
  const query = teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''
  return requestNoContent(user, teamId, `/integrations/secrets/${providerKey}/${encodeURIComponent(envName)}${query}`, {
    method: 'DELETE',
  })
}

export async function listIntegrationCustomEnv(user: KratosUser, teamId: string | null) {
  const query = teamId ? `?team_id=${encodeURIComponent(teamId)}` : ''
  return request<{ data: IntegrationCustomEnv[]; count: number }>(user, teamId, `/integrations/custom-env${query}`)
}

export async function createIntegrationCustomEnv(
  user: KratosUser,
  teamId: string | null,
  payload: {
    label: string
    env_name: string
    value: string
    is_secret?: boolean
    inject_to_sandbox?: boolean
    write_to_workspace_env?: boolean
    is_active?: boolean
  }
) {
  return request<IntegrationCustomEnv>(user, teamId, '/integrations/custom-env', {
    method: 'POST',
    body: JSON.stringify({
      ...payload,
      team_id: teamId,
    }),
  })
}

export async function patchIntegrationCustomEnv(
  user: KratosUser,
  teamId: string | null,
  envId: string,
  payload: {
    label?: string
    value?: string
    is_secret?: boolean
    inject_to_sandbox?: boolean
    write_to_workspace_env?: boolean
    is_active?: boolean
  }
) {
  return request<IntegrationCustomEnv>(user, teamId, `/integrations/custom-env/${envId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}

export async function deleteIntegrationCustomEnv(user: KratosUser, teamId: string | null, envId: string) {
  return requestNoContent(user, teamId, `/integrations/custom-env/${envId}`, {
    method: 'DELETE',
  })
}

